import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Checkbox,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { startOfToday, addDays, endOfMonth, parse, format } from "date-fns";
import useCall from "../../hooks/useCall";
import useLocalization from "../../hooks/useLocalization";
import usePage from "../../hooks/usePage";
import EntityList from "../pages/EntityList";
import CallDocketModal from "../modals/CallDocketModal";
import UserSearch from "../ui/UserSearch";
import Loading from "../ui/Loading";
import { exportCallsToExcel } from "../../utils/exportToExcel";

const OpsHealthCallsModal = ({
  open,
  onClose,
  metricType,
  filters,
  allIncompleteCalls,
  getAllIncompleteCalls,
  exportAllIncompleteCalls,
}) => {
  const {
    calls = [],
    getCalls,
    getCallById,
    getCallFullListById,
    updateCall,
  } = useCall();
  const { showMessage } = usePage();
  const { callStatus = {} } = useLocalization();
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCall, setSelectedCall] = useState(null);
  const [docketOpen, setDocketOpen] = useState(false);
  const [selectedCalls, setSelectedCalls] = useState([]);
  const [selectedRep, setSelectedRep] = useState("");
  const [isAssigning, setIsAssigning] = useState(false);
  const [orderBy, setOrderBy] = useState("");
  const [order, setOrder] = useState("asc");

  // Always use safe array for calls
  const effectiveCalls =
    metricType === "allIncomplete"
      ? Array.isArray(allIncompleteCalls?.data)
        ? allIncompleteCalls.data
        : []
      : Array.isArray(calls)
        ? calls
        : [];

  const filteredCalls = useMemo(() => {
    if (metricType === "allIncomplete") {
      return effectiveCalls;
    }

    const list = Array.isArray(effectiveCalls) ? effectiveCalls : [];
    if (!metricType) {
      return list;
    }

    const parseDateFromYyyymmdd = (value) => {
      if (!value || typeof value !== "string") return null;
      try {
        const parsed = parse(value, "yyyy-MM-dd", new Date());
        return Number.isNaN(parsed.getTime()) ? null : parsed;
      } catch {
        return null;
      }
    };

    const fromDate = filters?.startDate
      ? parseDateFromYyyymmdd(filters.startDate)
      : null;
    const toDate = filters?.endDate
      ? parseDateFromYyyymmdd(filters.endDate)
      : null;

    const now = new Date();
    const today = startOfToday();
    const tomorrow = addDays(today, 1);
    const dayAfterTomorrow = addDays(today, 2);

    const filterYear = Number(filters?.year) || now.getFullYear();
    const filterMonth = Number(filters?.month) || now.getMonth() + 1;
    const monthStart = new Date(filterYear, filterMonth - 1, 1);
    const monthEnd = new Date(filterYear, filterMonth, 0);
    const effectiveStart = fromDate || monthStart;
    const effectiveEnd = toDate || monthEnd;

    const inRange = (date) => {
      if (!date) return false;
      return date >= effectiveStart && date <= effectiveEnd;
    };

    return list.filter((call) => {
      const callDate = parseDateFromYyyymmdd(call?.yyyymmdd);
      const status = String(call?.callStatus || "").toLowerCase();

      switch (metricType) {
        case "incomplete":
          return status === "incomplete" && inRange(callDate);
        case "completed":
          return status === "complete" && inRange(callDate);
        case "today":
          return callDate && callDate >= today && callDate < tomorrow;
        case "tomorrow":
          return (
            callDate && callDate >= tomorrow && callDate < dayAfterTomorrow
          );
        case "restOfMonth":
          return (
            callDate && callDate >= dayAfterTomorrow && callDate <= effectiveEnd
          );
        case "overdue":
          return (
            status === "incomplete" &&
            callDate &&
            callDate >= effectiveStart &&
            callDate < today
          );
        case "unassigned":
          return !call?.userId && inRange(callDate);
        default:
          return true;
      }
    });
  }, [effectiveCalls, metricType, filters]);

  const hasCalls = filteredCalls.length > 0;

  // Load calls when modal opens or filters change

  useEffect(() => {
    if (open) {
      loadCalls();
    }
  }, [open, metricType, filters]);

  const loadCalls = async () => {
    try {
      setIsLoading(true);
      if (metricType === "allIncomplete") {
        await getAllIncompleteCalls(filters);
      } else {
        const filter = buildFilter();
        await getCalls(filter);
      }
    } catch (err) {
      console.error("[OpsHealthCallsModal] Error loading calls:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const buildFilter = () => {
    const baseFilter = {};

    // Use date range if provided, otherwise fall back to year/month
    let startOfFilterMonthStr, endOfFilterMonthStr;

    if (filters?.startDate && filters?.endDate) {
      // Date range is already in yyyy-MM-dd format
      startOfFilterMonthStr = filters.startDate;
      endOfFilterMonthStr = filters.endDate;
    } else {
      // Apply year/month filters for date range
      const year = filters?.year || new Date().getFullYear();
      const month = filters?.month || new Date().getMonth() + 1;
      const startOfFilterMonth = new Date(year, month - 1, 1);
      const endOfFilterMonth = new Date(year, month, 0);

      // Format dates as YYYY-MM-DD for yyyymmdd comparisons
      const formatYYYYMMDD = (date) => {
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, "0");
        const dd = String(date.getDate()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd}`;
      };

      startOfFilterMonthStr = formatYYYYMMDD(startOfFilterMonth);
      endOfFilterMonthStr = formatYYYYMMDD(endOfFilterMonth);
    }

    const today = startOfToday();
    const tomorrow = addDays(today, 1);
    const dayAfterTomorrow = addDays(today, 2);

    const formatYYYYMMDD = (date) => {
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, "0");
      const dd = String(date.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    };

    const todayStr = formatYYYYMMDD(today);
    const tomorrowStr = formatYYYYMMDD(tomorrow);
    const dayAfterTomorrowStr = formatYYYYMMDD(dayAfterTomorrow);

    // Always apply client filter if provided
    if (filters?.clientId) {
      baseFilter.clientId = filters.clientId;
    }

    // Apply project filter if provided
    if (filters?.projectId) {
      baseFilter.projectId = filters.projectId;
    }

    // Apply rep filter if provided (single repId)
    if (filters?.repId) {
      baseFilter.userId = filters.repId;
    }

    // Apply supervisor scope when selected (resolved server-side to managed reps)
    if (filters?.supervisorId) {
      baseFilter.supervisorId = filters.supervisorId;
    }

    // Filter out order-only calls to match backend logic
    baseFilter.callType = { $ne: "order" };

    // Do not exclude syndicated calls; server metrics now group by groupId

    switch (metricType) {
      case "incomplete":
        baseFilter.callStatus = "incomplete";
        baseFilter.yyyymmdd = {
          $gte: startOfFilterMonthStr,
          $lte: endOfFilterMonthStr,
        };
        break;

      case "completed":
        baseFilter.callStatus = "complete";
        baseFilter.yyyymmdd = {
          $gte: startOfFilterMonthStr,
          $lte: endOfFilterMonthStr,
        };
        break;

      case "today":
        baseFilter.yyyymmdd = { $gte: todayStr, $lt: tomorrowStr };
        break;

      case "tomorrow":
        baseFilter.yyyymmdd = { $gte: tomorrowStr, $lt: dayAfterTomorrowStr };
        break;

      case "restOfMonth":
        baseFilter.yyyymmdd = {
          $gte: dayAfterTomorrowStr,
          $lte: endOfFilterMonthStr,
        };
        break;

      case "overdue":
        baseFilter.callStatus = "incomplete";
        baseFilter.yyyymmdd = {
          $gte: startOfFilterMonthStr,
          $lt: todayStr,
        };
        break;

      case "unassigned":
        baseFilter.userId = null;
        baseFilter.yyyymmdd = {
          $gte: startOfFilterMonthStr,
          $lte: endOfFilterMonthStr,
        };
        break;

      default:
        break;
    }

    return baseFilter;
  };

  const getTitle = () => {
    switch (metricType) {
      case "incomplete":
        return "Incomplete Calls";
      case "completed":
        return "Completed Calls";
      case "today":
        return "Calls Scheduled Today";
      case "tomorrow":
        return "Calls Scheduled Tomorrow";
      case "restOfMonth":
        return "Calls Scheduled (Rest of Month)";
      case "overdue":
        return "Overdue Calls";
      case "unassigned":
        return "Unassigned Calls";
      case "allIncomplete":
        return "Incomplete Calls (All Dates)";
      default:
        return "Calls";
    }
  };

  const handleRefresh = () => {
    loadCalls();
  };

  const handleOpenDocket = async (call) => {
    const fallbackCall = call || null;
    const callId =
      fallbackCall?.callId || fallbackCall?.id || fallbackCall?._id;

    if (metricType === "allIncomplete" && callId) {
      try {
        const fullCall =
          (await getCallById(callId)) || (await getCallFullListById(callId));

        if (!fullCall) {
          showMessage("Unable to load call docket", "error");
          return;
        }

        setSelectedCall(fullCall);
      } catch (err) {
        showMessage("Unable to load call docket", "error");
        return;
      }
    } else {
      if (!fallbackCall) {
        showMessage("Unable to open call docket", "error");
        return;
      }
      setSelectedCall(fallbackCall);
    }

    setDocketOpen(true);
  };

  const handleCloseDocket = () => {
    setDocketOpen(false);
    setSelectedCall(null);
  };

  const handleSortChange = (field, direction) => {
    setOrderBy(field);
    setOrder(direction);
  };

  // Sort calls based on orderBy and order
  const sortedCalls = [...filteredCalls].sort((a, b) => {
    if (!orderBy) return 0;

    // Helper to get nested values from objects (e.g., "project.name")
    const getValue = (obj, path) => {
      return path.split(".").reduce((current, prop) => current?.[prop], obj);
    };

    const aVal = getValue(a, orderBy);
    const bVal = getValue(b, orderBy);

    // Handle null/undefined values
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return order === "asc" ? 1 : -1;
    if (bVal == null) return order === "asc" ? -1 : 1;

    // String comparison
    if (typeof aVal === "string" && typeof bVal === "string") {
      return order === "asc"
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    }

    // Number comparison
    if (order === "asc") {
      return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
    } else {
      return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
    }
  });

  const handleSelectCall = (call) => {
    setSelectedCalls((prev) => {
      const isSelected = prev.some((c) => c.id === call.id);
      if (isSelected) {
        return prev.filter((c) => c.id !== call.id);
      } else {
        return [...prev, call];
      }
    });
  };

  const handleSelectAll = () => {
    if (!filteredCalls || !Array.isArray(filteredCalls)) return;
    if (selectedCalls.length === filteredCalls.length) {
      setSelectedCalls([]);
    } else {
      setSelectedCalls([...filteredCalls]);
    }
  };

  const handleAssignRep = async () => {
    if (!selectedRep || selectedCalls.length === 0) return;

    setIsAssigning(true);
    try {
      // Update each selected call with the new rep
      const updatePromises = selectedCalls.map((call) =>
        updateCall({ id: call.id, userId: selectedRep }, false),
      );
      await Promise.all(updatePromises);

      // Clear selections and refresh
      setSelectedCalls([]);
      setSelectedRep("");
      await loadCalls();
    } catch (err) {
      console.error("[OpsHealthCallsModal] Error assigning rep:", err);
    } finally {
      setIsAssigning(false);
    }
  };

  const getCallStatusColor = (status) => {
    if (!status || !callStatus || !callStatus[status]) {
      return "#999";
    }
    return callStatus[status].color || "#999";
  };

  const formatFrequency = (frequency) => {
    if (!frequency || typeof frequency !== "object") return "-";

    const weekLabels = ["week1", "week2", "week3", "week4", "week5"]
      .map((weekKey, index) => {
        const value = String(frequency?.[weekKey] || "").trim();
        if (!value) return null;
        return `W${index + 1}: ${value}`;
      })
      .filter(Boolean);

    return weekLabels.length > 0 ? weekLabels.join(" | ") : "-";
  };

  // Define table columns
  const columns = [
    ...(metricType === "unassigned"
      ? [
          {
            name: "select",
            label: (
              <Checkbox
                checked={
                  filteredCalls.length > 0 &&
                  selectedCalls.length === filteredCalls.length
                }
                indeterminate={
                  selectedCalls.length > 0 &&
                  selectedCalls.length < filteredCalls.length
                }
                onChange={handleSelectAll}
              />
            ),
            width: "50px",
            sortable: false,
            render: (call) => (
              <Checkbox
                checked={selectedCalls.some((c) => c.id === call.id)}
                onChange={() => handleSelectCall(call)}
              />
            ),
          },
        ]
      : []),
    { name: "project.name", label: "Project", width: "150px" },
    { name: "location.name", label: "Location", width: "150px" },
    { name: "location.locationNumber", label: "Number", width: "80px" },
    {
      name: "location.address.streetAddress",
      label: "Street Address",
      width: "220px",
    },
    {
      name: "frequency",
      label: "Frequency",
      width: "220px",
      render: (call) => (
        <Typography sx={{ fontSize: "0.85rem" }}>
          {formatFrequency(call?.frequency)}
        </Typography>
      ),
    },
    { name: "location.address.city", label: "City", width: "100px" },
    { name: "location.address.province", label: "Province", width: "80px" },
    {
      name: "yyyymmdd",
      label: "Scheduled Date",
      width: "150px",
      render: (call) => {
        if (!call?.yyyymmdd) return <Typography>-</Typography>;
        try {
          // Parse "yyyy-MM-dd" format and format as "EEEE, MMMM d" (e.g., "Monday, January 28")
          const parsedDate = parse(call.yyyymmdd, "yyyy-MM-dd", new Date());
          const formattedDate = format(parsedDate, "EEEE, MMMM d");
          return (
            <Typography sx={{ fontSize: "0.9rem" }}>{formattedDate}</Typography>
          );
        } catch (err) {
          return <Typography>-</Typography>;
        }
      },
    },
    { name: "repName", label: "Rep", width: "120px" },
    {
      name: "callStatus",
      label: "Status",
      width: "100px",
      render: (call) => {
        if (!call?.callStatus) return <Typography>-</Typography>;
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                backgroundColor: getCallStatusColor(call.callStatus),
                width: "12px",
                height: "12px",
                borderRadius: "50%",
              }}
            />
            <Typography sx={{ fontSize: "0.85rem" }}>
              {call.callStatus}
            </Typography>
          </Box>
        );
      },
    },
    {
      name: "actions",
      label: "Action",
      width: "100px",
      sortable: false,
      render: (call) => (
        <Button
          size="small"
          variant="outlined"
          onClick={() => handleOpenDocket(call)}
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>{getTitle()}</DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Loading />
        ) : hasCalls ? (
          <Box sx={{ mt: 2 }}>
            {/* Bulk Assignment UI (only for unassigned metric) */}
            {metricType === "unassigned" && (
              <Box
                sx={{ mb: 3, display: "flex", gap: 2, alignItems: "center" }}
              >
                <Box sx={{ minWidth: 250 }}>
                  <UserSearch
                    value={selectedRep}
                    onChange={(userId) => setSelectedRep(userId)}
                    label="Assign to Rep"
                    disabled={selectedCalls.length === 0}
                    size="small"
                  />
                </Box>
                <Button
                  variant="contained"
                  onClick={handleAssignRep}
                  disabled={
                    !selectedRep || selectedCalls.length === 0 || isAssigning
                  }
                >
                  {isAssigning
                    ? "Assigning..."
                    : `Assign ${selectedCalls.length} Call${selectedCalls.length !== 1 ? "s" : ""}`}
                </Button>
              </Box>
            )}

            {/* Call Count */}
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 2 }}
            >
              Total: {filteredCalls.length} calls
            </Typography>

            {/* Calls Table */}
            {filteredCalls.length > 0 ? (
              <EntityList
                entityList={sortedCalls}
                columns={columns}
                tableHeight="60vh"
                orderBy={orderBy}
                order={order}
                onSortChange={handleSortChange}
              />
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
              >
                No calls found
              </Typography>
            )}
          </Box>
        ) : (
          <Box sx={{ mt: 2 }}>
            <Typography
              variant="body2"
              color="text.secondary"
            >
              No calls found
            </Typography>
          </Box>
        )}

        {/* CallDocket Modal */}
        {docketOpen && selectedCall && (
          <CallDocketModal
            call={selectedCall}
            open={docketOpen}
            onClose={handleCloseDocket}
            onRefresh={handleRefresh}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button
          startIcon={<FileDownloadIcon />}
          onClick={async () => {
            if (metricType === "allIncomplete") {
              const results = await exportAllIncompleteCalls(filters);
              if (results?.isSuccessful && Array.isArray(results?.data)) {
                exportCallsToExcel(results.data, metricType);
              } else {
                showMessage(results?.message || "Export failed");
              }
              return;
            }
            exportCallsToExcel(filteredCalls, metricType);
          }}
          disabled={!hasCalls}
        >
          Export to Excel
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default OpsHealthCallsModal;
